import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'

import {
  CustomerRequiredAction,
  CustomerRequiredActionSchema,
  ShipmentRequiredAction,
  ShipmentRequiredActionSchema,
} from '../domain/workbench'

const basePath = '/api/workbench'

export type CustomerRequiredActionPageParams = {
  customerId: string
  page?: number
  pageSize?: number
  search?: string
  types?: string
  dueDateFrom?: string
  dueDateTo?: string
  entityType?: string
  entityId?: string
}

export const workbenchAPIConfig: ApiConfig<{
  getRequiredActionsForCustomer: EndpointConfig<CustomerRequiredActionPageParams, CustomerRequiredAction>
  getRequiredActionsByEntity: EndpointConfig<
    { customerID: string; entityType: string; entityID: string },
    ShipmentRequiredAction
  >
}> = {
  getRequiredActionsForCustomer: {
    requestConfig: (params) => {
      const { customerId, ...query } = params

      return {
        endpoint: `${basePath}/required-actions/customer/${customerId}`,
        method: 'get',
        deserializationStrategy: CustomerRequiredActionSchema,
        options: {
          params: Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined)),
        },
      }
    },
  },
  getRequiredActionsByEntity: {
    requestConfig: ({ customerID, entityType, entityID }) => ({
      endpoint: `${basePath}/required-actions/customer/${customerID}/${entityType}/${entityID}`,
      method: 'get',
      deserializationStrategy: ShipmentRequiredActionSchema,
    }),
  },
}

export const workbenchClient = clientFactory(workbenchAPIConfig)

export const requiredActionsQueryOptions = (params: CustomerRequiredActionPageParams) =>
  queryOptions({
    queryKey: ['required-actions', params],
    queryFn: () => workbenchClient.getRequiredActionsForCustomer(params),
  })

export const entityRequiredActionsQueryOptions = (customerID: string, entityType: string, entityID: string) =>
  queryOptions({
    queryKey: ['required-actions', customerID, entityType, entityID],
    queryFn: () =>
      workbenchClient.getRequiredActionsByEntity({
        customerID,
        entityType,
        entityID,
      }),
  })

export const useRequiredActionsForCustomer = (params: CustomerRequiredActionPageParams) =>
  useSuspenseQuery(requiredActionsQueryOptions(params))

export const useRequiredActionsByEntity = (customerID: string, entityType: string, entityID: string) =>
  useSuspenseQuery(entityRequiredActionsQueryOptions(customerID, entityType, entityID))
